import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from '@tanstack/react-form';
import { toast } from 'sonner';
import * as z from 'zod';
import { FormTextField } from '@/components/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUserInvitation } from '../../invitations/hooks/useUserInvitation';
import { useAcceptUserInvitation } from '../../../hooks/useAcceptUserInvitation';
import LoadingState from './components/LoadingState';
import InvalidInvitationState from './components/InvalidInvitationState';
import ExpiredInvitationState from './components/ExpiredInvitationState';

const formSchema = z
  .object({
    fullName: z.string().min(1, 'Nome completo é obrigatório'),
    password: z.string().min(6, 'A senha deve ter pelo menos 6 caracteres'),
    passwordConfirmation: z.string().min(1, 'Confirmação de senha é obrigatória'),
  })
  .refine((data) => data.password === data.passwordConfirmation, {
    message: 'As senhas não coincidem',
    path: ['passwordConfirmation'],
  });

export default function AcceptUserInvitation() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const { invitation, loading } = useUserInvitation(token!);
  const { acceptInvitation, loading: acceptLoading } = useAcceptUserInvitation({
    onCompleted: () => {
      toast.success('Conta criada com sucesso! Por favor, faça login.');
      navigate('/login');
    },
  });

  const form = useForm({
    defaultValues: {
      fullName: '',
      password: '',
      passwordConfirmation: '',
    },
    validators: {
      onSubmit: formSchema,
    },
    onSubmit: ({ value }) => {
      return acceptInvitation(token!, value.fullName, value.password, value.passwordConfirmation);
    },
  });

  if (loading) {
    return <LoadingState />;
  }

  if (!invitation) {
    return <InvalidInvitationState onNavigateToLogin={() => navigate('/login')} />;
  }

  const isExpired = new Date(invitation.expiresAt) < new Date();
  const isUsed = invitation.status !== 'PENDING';

  if (isExpired || isUsed) {
    return <ExpiredInvitationState isExpired={isExpired} onNavigateToLogin={() => navigate('/login')} />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary-50 dark:bg-primary-950 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-primary-900 border border-primary-200 dark:border-primary-800 p-8 sm:p-12">
          <div className="mb-8">
            <h1 className="text-2xl font-light tracking-tight text-primary-950 dark:text-white">
              Crie sua Conta
            </h1>
            <p className="mt-2 text-sm text-primary-600 dark:text-primary-400">
              Você foi convidado a participar do MARQ. Complete seu cadastro abaixo.
            </p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
            className="space-y-6"
          >
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400">
                E-mail
              </Label>
              <Input
                type="email"
                value={invitation.email}
                disabled
                className="rounded-none border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-950"
              />
            </div>

            <FormTextField form={form} name="fullName" label="Nome Completo" />
            <FormTextField form={form} name="password" label="Senha" type="password" />
            <FormTextField form={form} name="passwordConfirmation" label="Confirmar Senha" type="password" />

            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
            >
              {([canSubmit, isSubmitting]) => (
                <Button
                  type="submit"
                  disabled={!canSubmit || acceptLoading}
                  className="w-full rounded-none uppercase tracking-widest text-sm"
                >
                  {isSubmitting || acceptLoading ? 'Criando Conta...' : 'Criar Conta'}
                </Button>
              )}
            </form.Subscribe>
          </form>
        </div>
      </div>
    </div>
  );
}
